import { useRef, useState } from "react";
import { validateAsset, isPublishable, type AssetSpec } from "@greg-chick/shared";
import { readUpload } from "../lib/imageMeta";
import { uploadAsset, type ManifestEntry } from "../lib/contentApi";
import { propKeyFor, propNameFromFileName } from "./propNaming";

/**
 * **내 컴퓨터에서 소품 그림 찾아 올리기.**
 *
 * ## 왜 이 순서인가 — 파일 먼저, 이름은 자동
 * 예전엔 이름을 먼저 쳐야 파일 고르는 칸이 나왔다. 운영자가 하려는 일은 "폴더에서 그림을
 * 찾는 것"인데 이름부터 물으니 순서가 거꾸로였다. 이제 **[📁 파일 찾기]가 바로 OS 파일 창**을
 * 열고, 고른 파일 이름에서 소품 이름을 자동으로 만든다(`바위.png` → "바위"). 고치고 싶으면 고친다.
 *
 * ## 브라우저가 못 하는 것
 * 웹페이지는 사용자 드라이브의 **폴더 트리를 직접 그릴 수 없다**(보안). 할 수 있는 건 둘:
 *  - OS 파일 창 — 어디서나 된다. 탐색기가 그대로 열려 폴더 이동·검색이 다 된다
 *  - 폴더 통째로 열기(`showDirectoryPicker`) — 폴더 안 그림을 앱 격자로 보여준다.
 *    **크롬·엣지에서만** 되므로, 안 되는 브라우저에서는 버튼을 아예 감춘다(눌러도 안 되는
 *    버튼을 보여주면 고장으로 보인다).
 */

export interface PropFilePickerProps {
  spec: AssetSpec;
  /** 이미 있는 소품 키들 — 이름이 겹치면 뒤에 번호를 붙인다(덮어쓰지 않는다). */
  takenKeys: string[];
  /** 한 장 올라갈 때마다. 호출부가 매니페스트에 넣고 저장 바에 쌓는다. */
  onAdded: (key: string, entry: ManifestEntry) => void;
  /** 한 장만 올렸을 때 그 오브젝트에 바로 붙일지 — 여러 장이면 부르지 않는다. */
  onPickOne?: (key: string) => void;
}

interface Row {
  name: string;
  key: string;
  status: "올리는 중" | "됨" | string;
}

/** 폴더 열기를 지원하는 브라우저인가(크롬·엣지). */
const canOpenFolder = (): boolean =>
  typeof window !== "undefined" && "showDirectoryPicker" in window;

export function PropFilePicker({ spec, takenKeys, onAdded, onPickOne }: PropFilePickerProps) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [rows, setRows] = useState<Row[]>([]);
  const [busy, setBusy] = useState(false);
  /** 폴더에서 읽어 온 그림들(아직 안 올림) — 격자에서 골라 올린다. */
  const [folder, setFolder] = useState<{ file: File; url: string }[]>([]);

  /** 이번 작업 안에서도 이름이 겹치지 않게, 방금 만든 키까지 합쳐서 센다. */
  async function uploadAll(files: File[]): Promise<void> {
    setBusy(true);
    const taken = [...takenKeys];
    const out: Row[] = [];
    let lastKey: string | null = null;
    for (const file of files) {
      const name = propNameFromFileName(file.name);
      const key = propKeyFor(name, taken);
      taken.push(key);
      try {
        const meta = await readUpload(file);
        const issues = validateAsset(spec, meta);
        if (!isPublishable(issues)) {
          out.push({ name, key, status: issues[0]?.message ?? "규격에 안 맞아요" });
          continue;
        }
        const url = await uploadAsset(key, file);
        onAdded(key, { url, alt: name });
        lastKey = key;
        out.push({ name, key, status: "됨" });
      } catch {
        out.push({ name, key, status: "서버에 연결이 안 돼요" });
      }
      setRows([...out]);
    }
    setRows(out);
    setBusy(false);
    // 한 장만 올렸으면 그 오브젝트에 바로 붙인다(여러 장은 무엇을 붙일지 알 수 없다)
    if (files.length === 1 && lastKey) onPickOne?.(lastKey);
  }

  async function openFolder(): Promise<void> {
    type DirHandle = { values: () => AsyncIterable<{ kind: string; getFile: () => Promise<File> }> };
    const pick = (window as unknown as { showDirectoryPicker: () => Promise<DirHandle> }).showDirectoryPicker;
    let dir: DirHandle;
    try {
      dir = await pick();
    } catch {
      return; // 사용자가 취소 — 아무 일도 안 일어난다
    }
    const found: { file: File; url: string }[] = [];
    for await (const h of dir.values()) {
      if (h.kind !== "file") continue;
      const f = await h.getFile();
      if (!/\.(png|jpe?g|webp)$/i.test(f.name)) continue;
      found.push({ file: f, url: URL.createObjectURL(f) });
      if (found.length >= 60) break; // 너무 많으면 화면이 감당 못 한다
    }
    setFolder(found);
  }

  return (
    <div className="propfile">
      <div className="propfile__btns">
        <button type="button" className="btn" disabled={busy} onClick={() => fileRef.current?.click()}>
          {busy ? "올리는 중…" : "📁 파일 찾기"}
        </button>
        {canOpenFolder() ? (
          <button type="button" className="btn" disabled={busy} onClick={() => void openFolder()}>
            📂 폴더 열기
          </button>
        ) : null}
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          multiple
          hidden
          onChange={(e) => {
            const fs = [...(e.target.files ?? [])];
            e.target.value = ""; // 같은 파일을 다시 골라도 동작하게
            if (fs.length) void uploadAll(fs);
          }}
        />
      </div>
      <p className="zone__tip">
        여러 장을 한 번에 고를 수 있어요. 이름은 <b>파일 이름</b>에서 자동으로 만들어져요(바위.png → 바위).
      </p>

      {folder.length > 0 ? (
        <div className="propfile__folder">
          <p className="zone__tip">폴더에서 {folder.length}장을 찾았어요 — 쓸 것을 고르세요.</p>
          <div className="objimg__grid">
            {folder.map((f) => (
              <button
                key={f.file.name}
                type="button"
                className="objimg__opt"
                title={f.file.name}
                disabled={busy}
                onClick={() => {
                  setFolder([]);
                  void uploadAll([f.file]);
                }}
              >
                <img src={f.url} alt="" />
                <span>{f.file.name}</span>
              </button>
            ))}
          </div>
        </div>
      ) : null}

      {rows.length > 0 ? (
        <ul className="propfile__rows">
          {rows.map((r) => (
            <li key={r.key} data-ok={r.status === "됨" || undefined}>
              {r.status === "됨" ? "✓" : r.status === "올리는 중" ? "…" : "⚠️"} {r.name}
              {r.status === "됨" ? null : <small> — {r.status}</small>}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
